import type {
  AnnualTimetableCell,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import {
  getAnnualTimetableCells,
  findAnnualTimetableCellsLocalCacheForGrade,
  loadAnnualTimetableGradeCellsLocalCache,
} from '../services/annualTimetableService';
import type { ClassScheduleSettings } from '../services/sectionLayoutService';
import { loadAnnualProgressDataCache } from '../utils/annualProgressDataCache';
import { loadAnnualProgressScopedTermCache } from '../utils/annualProgressScopedTermCache';
import { loadProgramFixedContentRowsCache } from '../utils/programFixedContentRowsCache';
import {
  loadProgramProgressCellsCache,
  type ProgramProgressCellsCacheCoverage,
} from '../utils/programProgressCellsCache';
import type { AnnualProgressMetaBundle } from './hooks/types';
import { annualProgressMetaQueryOptions } from './hooks/useAnnualProgressMetaQuery';
import { annualTimetableCellsQueryOptions } from './hooks/useAnnualTimetableCellsQuery';
import { schoolKeys } from './keys';
import { queryClient } from './queryClient';

function preferRicherQueryOrLs<T>(
  fromQuery: T[] | undefined,
  ls: { found: boolean; rows: T[] }
): { found: boolean; rows: T[] } {
  const q = fromQuery;
  const lsLen = ls.found ? ls.rows.length : 0;
  if (q != null && q.length > lsLen) return { found: true, rows: q };
  if (ls.found) return ls;
  if (q != null) return { found: true, rows: q };
  return { found: false, rows: [] };
}

/** React Query in-memory 캐시 우선, miss 시 LS fallback */
export function readProgramScopedRowsCacheOrQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: ProgramScopedContentRow[] } {
  const fromQuery = queryClient.getQueryData<ProgramScopedContentRow[]>(
    schoolKeys.programScopedRows(workspaceId, academicYear, termNumber, gradeId)
  );
  return preferRicherQueryOrLs(
    fromQuery,
    loadAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId)
  );
}

export function readProgramFixedRowsCacheOrQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: ProgramFixedContentRow[] } {
  const fromQuery = queryClient.getQueryData<ProgramFixedContentRow[]>(
    schoolKeys.programFixedRows(workspaceId, academicYear, termNumber, gradeId)
  );
  return preferRicherQueryOrLs(
    fromQuery,
    loadProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId)
  );
}

export function readProgramProgressCellsCacheOrQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: ProgramProgressCell[]; coverage: ProgramProgressCellsCacheCoverage } {
  const fromQuery = queryClient.getQueryData<ProgramProgressCell[]>(
    schoolKeys.programProgressCells(workspaceId, academicYear, termNumber, gradeId)
  );
  const ls = loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId);
  const picked = preferRicherQueryOrLs(fromQuery, ls);
  const coverage: ProgramProgressCellsCacheCoverage =
    picked.found && picked.rows === ls.rows ? ls.coverage : picked.found ? 'full' : ls.coverage;
  return { ...picked, coverage };
}

/** 대시보드·연간진도표와 동일 키의 연간시간표 셀 (RQ → LS) */
export function readAnnualTimetableCellsCacheOrQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: AnnualTimetableCell[] } {
  const fromQuery = queryClient.getQueryData<AnnualTimetableCell[]>(
    schoolKeys.annualTimetableCells(workspaceId, academicYear, termNumber, gradeId)
  );
  return preferRicherQueryOrLs(
    fromQuery,
    loadAnnualTimetableGradeCellsLocalCache(workspaceId, academicYear, termNumber, gradeId)
  );
}

type TimetableCellsHit = {
  found: boolean;
  rows: AnnualTimetableCell[];
  academicYear: number;
  termNumber: 1 | 2;
};

function scoreTimetableHit(
  academicYear: number,
  termNumber: 1 | 2,
  rowCount: number,
  preferred?: { academicYear: number; termNumber: 1 | 2 }
): number {
  let score = rowCount;
  if (preferred) {
    if (academicYear === preferred.academicYear) score += 1_000_000;
    if (termNumber === preferred.termNumber) score += 100_000;
    score -= Math.abs(academicYear - preferred.academicYear) * 1_000;
  }
  return score;
}

/**
 * 메인 프레젠 cacheOnly: 대시보드가 RQ persist에만 둔 연간시간표 셀을 grade 기준으로 스캔.
 * (학년도·학기 추정값이 달라도 실제 워밍된 키를 찾음)
 */
export function findAnnualTimetableCellsCacheOrQueryForGrade(
  workspaceId: string,
  gradeId: string,
  preferred?: { academicYear: number; termNumber: 1 | 2 }
): TimetableCellsHit {
  const sid = workspaceId.trim();
  const gid = gradeId.trim();
  const empty: TimetableCellsHit = {
    found: false,
    rows: [],
    academicYear: preferred?.academicYear ?? new Date().getFullYear(),
    termNumber: preferred?.termNumber ?? 1,
  };
  if (!sid || !gid) return empty;

  let best: (TimetableCellsHit & { score: number }) | null = null;
  const consider = (academicYear: number, termNumber: 1 | 2, rows: AnnualTimetableCell[]) => {
    if (!rows.length) return;
    const score = scoreTimetableHit(academicYear, termNumber, rows.length, preferred);
    if (!best || score > best.score) {
      best = { found: true, rows, academicYear, termNumber, score };
    }
  };

  if (preferred) {
    const exact = readAnnualTimetableCellsCacheOrQuery(sid, preferred.academicYear, preferred.termNumber, gid);
    if (exact.found) consider(preferred.academicYear, preferred.termNumber, exact.rows);
    const other: 1 | 2 = preferred.termNumber === 1 ? 2 : 1;
    const otherHit = readAnnualTimetableCellsCacheOrQuery(sid, preferred.academicYear, other, gid);
    if (otherHit.found) consider(preferred.academicYear, other, otherHit.rows);
  }

  /* RQ persist hydrate 후: 동일 workspace·grade의 annualTimetableCells 전부 스캔 */
  try {
    const matches = queryClient.getQueriesData<AnnualTimetableCell[]>({
      queryKey: ['school', sid, 'annualTimetableCells'],
    });
    for (const [key, data] of matches) {
      if (!Array.isArray(data) || data.length === 0) continue;
      if (!Array.isArray(key) || key.length < 6) continue;
      if (key[0] !== 'school' || key[1] !== sid || key[2] !== 'annualTimetableCells') continue;
      if (String(key[5] ?? '') !== gid) continue;
      const academicYear = Number(key[3]);
      const termNumber = Number(key[4]) as 1 | 2;
      if (!Number.isFinite(academicYear) || (termNumber !== 1 && termNumber !== 2)) continue;
      consider(academicYear, termNumber, data);
    }
  } catch {
    /* ignore */
  }

  /* 연간진도표 bulk RQ: Record<cellKey, cell> — grade 필터 후 배열로 */
  try {
    const bulkMatches = queryClient.getQueriesData<Record<string, AnnualTimetableCell>>({
      queryKey: ['school', sid, 'annualTimetableCellsBulk'],
    });
    for (const [key, data] of bulkMatches) {
      if (!data || typeof data !== 'object' || Array.isArray(data)) continue;
      if (!Array.isArray(key) || key.length < 5) continue;
      if (key[0] !== 'school' || key[1] !== sid || key[2] !== 'annualTimetableCellsBulk') continue;
      const academicYear = Number(key[3]);
      const termNumber = Number(key[4]) as 1 | 2;
      if (!Number.isFinite(academicYear) || (termNumber !== 1 && termNumber !== 2)) continue;
      const rows = Object.values(data).filter((row) => row && (row.grade_id ?? '') === gid);
      if (rows.length > 0) consider(academicYear, termNumber, rows);
    }
  } catch {
    /* ignore */
  }

  /* LS 스캔 (RQ miss·hydrate 전 대비) */
  const fromLs = findAnnualTimetableCellsLocalCacheForGrade(sid, gid, preferred);
  if (fromLs.found) consider(fromLs.academicYear, fromLs.termNumber, fromLs.rows);

  return best ?? empty;
}

const EMPTY_ANNUAL_PROGRESS_META: AnnualProgressMetaBundle = {
  terms: [],
  schedule: null,
  tracks: [],
  regularColors: [],
  dedicatedColors: [],
  baseExcludeColors: [],
};

function metaBundleLooksWarm(data: AnnualProgressMetaBundle): boolean {
  return data.terms.length > 0 || data.schedule != null || data.tracks.length > 0;
}

/** 대시보드·연간진도표와 동일 키의 annualProgressMeta (RQ → LS) */
export function readAnnualProgressMetaCacheOrQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; data: AnnualProgressMetaBundle } {
  const fromQuery = queryClient.getQueryData<AnnualProgressMetaBundle>(
    schoolKeys.annualProgressMeta(workspaceId, academicYear, termNumber, gradeId)
  );
  if (fromQuery != null) {
    return { found: true, data: fromQuery };
  }
  const cached = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
  if (!cached.found) {
    return { found: false, data: EMPTY_ANNUAL_PROGRESS_META };
  }
  const { state } = cached;
  return {
    found: true,
    data: {
      terms: state.terms,
      schedule: state.schedule,
      tracks: state.tracks,
      regularColors: state.regularColors,
      dedicatedColors: state.dedicatedColors,
      baseExcludeColors: state.baseExcludeColors,
    },
  };
}

/**
 * 메인 프레젠 cacheOnly용: term 1·2·인접 학년도까지 RQ/LS meta를 스캔.
 * (대시보드는 meta를 term1 RQ에, 연간진도는 현재 학기 LS에 둘 수 있음)
 */
export function readAnnualProgressMetaCacheOrQueryWithFallback(
  workspaceId: string,
  academicYear: number,
  gradeId: string
): { found: boolean; data: AnnualProgressMetaBundle; academicYear: number; termNumber: 1 | 2 } {
  const years = [academicYear, academicYear - 1, academicYear + 1];
  for (const y of years) {
    for (const termNumber of [1, 2] as const) {
      const hit = readAnnualProgressMetaCacheOrQuery(workspaceId, y, termNumber, gradeId);
      if (hit.found && metaBundleLooksWarm(hit.data)) {
        return { found: true, data: hit.data, academicYear: y, termNumber };
      }
    }
  }
  return {
    found: false,
    data: EMPTY_ANNUAL_PROGRESS_META,
    academicYear,
    termNumber: 1,
  };
}

/**
 * 대시보드 `useAnnualTimetableCellsQuery`와 동일 키로 셀을 확보한다.
 * 캐시 hit면 네트워크 없이 반환, miss·forceNetwork면 같은 queryFn으로 fetch.
 */
export async function ensureAnnualTimetableCellsQueryData(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  forceNetwork?: boolean;
}): Promise<AnnualTimetableCell[]> {
  const { workspaceId, academicYear, termNumber, gradeId, forceNetwork = false } = params;
  const key = schoolKeys.annualTimetableCells(workspaceId, academicYear, termNumber, gradeId);
  if (!forceNetwork) {
    const cached = queryClient.getQueryData<AnnualTimetableCell[]>(key);
    if (cached) return cached;
    return queryClient.ensureQueryData(
      annualTimetableCellsQueryOptions({
        workspaceId,
        academicYear,
        termNumber,
        gradeId,
        forceNetwork: false,
      })
    );
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: () =>
      getAnnualTimetableCells(workspaceId, academicYear, termNumber, [gradeId], {
        forceNetwork: true,
      }),
  });
}

/** 대시보드 `useAnnualProgressMetaQuery`와 동일 키로 meta(수업시간 포함)를 확보 */
export async function ensureAnnualProgressMetaQueryData(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  forceNetwork?: boolean;
}): Promise<AnnualProgressMetaBundle> {
  const { workspaceId, academicYear, termNumber, gradeId, forceNetwork = false } = params;
  const key = schoolKeys.annualProgressMeta(workspaceId, academicYear, termNumber, gradeId);
  if (!forceNetwork) {
    const cached = queryClient.getQueryData<AnnualProgressMetaBundle>(key);
    if (cached) return cached;
    return queryClient.ensureQueryData(
      annualProgressMetaQueryOptions({
        workspaceId,
        academicYear,
        termNumber,
        gradeId,
      })
    );
  }
  const opts = annualProgressMetaQueryOptions({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
  });
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: opts.queryFn,
  });
}

export function readClassScheduleFromMetaCacheOrQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): ClassScheduleSettings | null {
  const meta = readAnnualProgressMetaCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  return meta.data.schedule ?? null;
}
